import {
  PairName,
  pairNamesByChain,
} from '@ui/assets/config/trade-pair-listings';
import { getChainName } from '@ui/constants/chain';
import { useDataChainId } from '@ui/hooks/datachain';
import { useBackendPairsAll } from '@ui/services/backend/hooks/getPairs/useBackendPairsAll';
import { CHAIN_NAMES, ChainId, ChainName } from '@ui/types/chain';
import { Nullable } from '@ui/types/utility';
import { objectFromEntries } from '@ui/utils/objectFromEntries';
import { addMilliseconds } from 'date-fns';
import { merge } from 'lodash-es';
import { useEffect, useMemo, useRef } from 'react';
import { create } from 'zustand';

import { MS_COUNTDOWN_BUFFER } from '../../config';
import { MarketSchedulePerPair, PairMarketStatus } from './type';
import { MarketWorkerCommand, MarketWorkerResponse } from './worker';

type Store = {
  _marketSchedule: Partial<Record<ChainName, MarketSchedulePerPair>>;
  initializeFromBackendConfig: (
    config: {
      nextOpen: Nullable<Date>;
      nextClose: Nullable<Date>;
      isOpen: Nullable<boolean>;
      schedule: Nullable<string>;
      pairName: PairName;
    }[],
    chainId: ChainId,
  ) => void;
  updateMarketSchedule: (
    update: Partial<MarketSchedulePerPair>,
    chainId: ChainId,
  ) => void;
  getMarketScheduleByPair: (
    pair: PairName,
    chainId: ChainId,
  ) => PairMarketStatus;
};

const defaultState: PairMarketStatus = {
  isClientOpen: true,
};
const defaultValue = objectFromEntries(
  CHAIN_NAMES.map((name) => {
    const pairNames = pairNamesByChain[name] ?? [];
    
return [
      name,
      objectFromEntries(
        pairNames.map((pair) => [pair, defaultState]),
      ),
    ] as const;
  }),
);

export const usePairMarketScheduleStore = create<Store>((set, get) => ({
  _marketSchedule: defaultValue,
  initializeFromBackendConfig(backendConfigs, chainId) {
    const chainName = getChainName(chainId);
    const pairConfigs = backendConfigs.map((config) => {
      return [
        config.pairName,
        {
          isClientOpen: config.isOpen,

          /**
           * client open and close time has a buffer before or after actual time
           */
          nextOpen: config.nextOpen
            ? addMilliseconds(config.nextOpen, MS_COUNTDOWN_BUFFER)
            : null,
          nextClose: config.nextClose
            ? addMilliseconds(config.nextClose, -MS_COUNTDOWN_BUFFER)
            : null,
          schedule: config.schedule,
        },
      ] as const;
    });

    const configMap = objectFromEntries(pairConfigs);

    const merged = merge({}, get()._marketSchedule[chainName], configMap);

    set({
      _marketSchedule: {
        ...get()._marketSchedule,
        [chainName]: {
          ...get()._marketSchedule[chainName],
          ...merged,
        },
      },
    });
  },
  updateMarketSchedule(update, chainId) {
    const chainName = getChainName(chainId);
    const scheduleMap = get()._marketSchedule[chainName];

    const merged = merge({}, scheduleMap, update);
    set({
      _marketSchedule: {
        ...get()._marketSchedule,
        [chainName]: {
          ...scheduleMap,
          ...merged,
        },
      },
    });
  },
  getMarketScheduleByPair(pair, chainId) {
    return get()._marketSchedule[getChainName(chainId)]?.[pair] ?? defaultState;
  },
}));

export const useInitPairMarketScheduleWorker = () => {
  const workerRef = useRef<Worker>();
  const chainId = useDataChainId();
  const pairSchedules = usePairMarketScheduleStore(
    (s) => s._marketSchedule[getChainName(chainId)],
  );

  useEffect(() => {
    workerRef.current = new Worker(new URL('./worker.ts', import.meta.url));

    workerRef.current?.postMessage({
      type: 'start',
      data: pairSchedules,
      chainId,
    } satisfies MarketWorkerCommand);

    workerRef.current.onmessage = (
      event: MessageEvent<MarketWorkerResponse>,
    ) => {
      if (event.data.type === 'update') {
        usePairMarketScheduleStore
          .getState()
          .updateMarketSchedule(event.data.data, event.data.chainId);
      }
    };

    return () => {
      workerRef.current?.postMessage({
        type: 'stop',
        chainId,
      } satisfies MarketWorkerCommand);
      workerRef.current?.terminate();
    };
  }, [chainId, pairSchedules]);
};

export const useSyncBackendConfig = () => {
  const { data: pairInfos } = useBackendPairsAll();

  const chainId = useDataChainId();

  const configs = useMemo(() => {
    if (!pairInfos) return [];

    return Object.values(pairInfos)
      .map((pair) => {
        if (!pair.schedule) return null;

        return {
          pairName: pair.pairName,
          isOpen: pair.isOpen,
          nextClose: pair.nextClose,
          nextOpen: pair.nextOpen,
          schedule: pair.schedule,
        };
      })
      .filter((v): v is NonNullable<typeof v> => !!v);
  }, [pairInfos]);

  useEffect(() => {
    usePairMarketScheduleStore
      .getState()
      .initializeFromBackendConfig(configs, chainId);
  }, [chainId, configs]);
};
